import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalTextArea } from '../../../components/GlobalComponents/GlobalTextArea';
import { GlobalDropdownField, DropdownOption } from '../../../components/GlobalComponents/GlobalDropdownField';
import type { PracticalMaterial } from '../PracticalMaterials';

interface AddPracticalMaterialModalProps {
  visible: boolean;
  courseId: string;
  chapterId: string | null; // null → course-general material
  editingMaterial?: PracticalMaterial | null;
  onHide: () => void;
  onSuccess?: () => void;
}

interface UploadedFile { file: File; name: string; size: number; }

// ── Design tokens (matches Notice modals / PracticalMaterialsBase) ─────────────
const C = {
  navy:       '#0e225d',
  navyMid:    '#1a3370',
  navyDark:   '#0a1a3d',
  gold:       '#fbb21f',
  goldLight:  '#ffc93c',
  goldDim:    'rgba(251,178,31,0.18)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white:      '#ffffff',
  border:     '#E5E7EB',
  bg:         '#F5F7FA',
  muted:      '#9ca3af',
  sub:        '#6b7280',
  body:       '#1f2937',
};

const AddPracticalMaterialModal: React.FC<AddPracticalMaterialModalProps> = ({
  visible,
  courseId,
  chapterId,
  editingMaterial,
  onHide,
  onSuccess,
}) => {
  const toast        = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading,        setLoading]        = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error,          setError]          = useState<string | null>(null);
  const [uploadedFiles,  setUploadedFiles]  = useState<UploadedFile[]>([]);

  const [formData, setFormData] = useState({
    title: '',
    body: '',
    status: 'active',
    order: 1,
    video_links: [''],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const MAX_FILES         = 3;
  const MAX_TOTAL_SIZE    = 20 * 1024 * 1024;
  const ALLOWED_FORMATS   = [
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg', 'image/jpg', 'image/png',
  ];
  const ALLOWED_EXTENSIONS = ['pdf', 'xls', 'xlsx', 'doc', 'docx', 'jpeg', 'jpg', 'png'];

  const statusOptions: DropdownOption[] = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Draft', value: 'draft' },
  ];

  const showToast = (severity: 'success' | 'error', summary: string, detail: string, life = 3500) =>
    toast.current?.show({ severity, summary, detail, life });

  useEffect(() => {
    if (editingMaterial) {
      setFormData({
        title: editingMaterial.title,
        body: editingMaterial.body,
        status: editingMaterial.status || 'active',
        order: editingMaterial.order ?? 1,
        video_links: editingMaterial.video_links.length > 0 ? editingMaterial.video_links : [''],
      });
    } else {
      setFormData({ title: '', body: '', status: 'active', order: 1, video_links: [''] });
    }
    setUploadedFiles([]);
    setErrors({});
    setError(null);
    setUploadProgress(0);
  }, [editingMaterial, visible]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleVideoLinkChange = (index: number, value: string) => {
    const newLinks = [...formData.video_links];
    newLinks[index] = value;
    setFormData((prev) => ({ ...prev, video_links: newLinks }));
  };
  const addVideoLink = () => {
    if (formData.video_links.length < 5) setFormData((prev) => ({ ...prev, video_links: [...prev.video_links, ''] }));
  };
  const removeVideoLink = (index: number) => {
    const newLinks = formData.video_links.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, video_links: newLinks.length > 0 ? newLinks : [''] }));
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };
  const getFileExtension = (filename: string): string => filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();
  const getTotalFilesSize = (): number => uploadedFiles.reduce((total, f) => total + f.size, 0);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    if (uploadedFiles.length + files.length > MAX_FILES) {
      showToast('error', 'Limit', `Maximum ${MAX_FILES} files allowed`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const newFiles: UploadedFile[] = [];
    const fileErrors: string[] = [];
    Array.from(files).forEach((file) => {
      const ext = getFileExtension(file.name);
      if (!ALLOWED_FORMATS.includes(file.type) && !ALLOWED_EXTENSIONS.includes(ext)) {
        fileErrors.push(`${file.name}: Invalid format`);
        return;
      }
      newFiles.push({ file, name: file.name, size: file.size });
    });
    const wouldBeTotal = getTotalFilesSize() + newFiles.reduce((s, f) => s + f.size, 0);
    if (wouldBeTotal > MAX_TOTAL_SIZE) {
      showToast('error', 'Size Limit', `Total size cannot exceed ${formatFileSize(MAX_TOTAL_SIZE)}`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (fileErrors.length > 0) showToast('error', 'Invalid Files', fileErrors.join(', '));
    setUploadedFiles((prev) => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const handleRemoveFile = (index: number) => setUploadedFiles((prev) => prev.filter((_, i) => i !== index));

  const getFileIcon = (filename: string): string => {
    const ext = getFileExtension(filename);
    switch (ext) {
      case 'pdf': return 'pi pi-file-pdf';
      case 'doc': case 'docx': return 'pi pi-file-word';
      case 'xls': case 'xlsx': return 'pi pi-file-excel';
      case 'jpg': case 'jpeg': case 'png': return 'pi pi-image';
      default: return 'pi pi-file';
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.body.trim()) newErrors.body = 'Content is required';
    if (!formData.status) newErrors.status = 'Status is required';
    if (!formData.order || formData.order < 1) newErrors.order = 'Order must be a positive number';

    const validLinks = formData.video_links.filter((l) => l.trim() !== '');
    if (validLinks.length > 5) newErrors.video_links = 'Maximum 5 video links allowed';
    for (const link of validLinks) {
      try { new URL(link); } catch {
        newErrors.video_links = 'All video links must be valid URLs';
        break;
      }
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      showToast('error', 'Validation Failed', 'Please correct all highlighted fields');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    setUploadProgress(0);

    const payload = new FormData();
    payload.append('course_id', courseId);
    if (chapterId) payload.append('chapter_id', chapterId); // omitted entirely → course-general
    payload.append('title', formData.title.trim());
    payload.append('body', formData.body.trim());
    payload.append('status', formData.status);
    payload.append('order', String(formData.order));

    formData.video_links.filter((l) => l.trim() !== '').forEach((link) => payload.append('video_links[]', link.trim()));
    uploadedFiles.forEach((f) => payload.append('files[]', f.file));

    try {
      const isEditing = !!editingMaterial;
      const response = isEditing
        ? await apiClient.put(`/v1/practical-materials/${editingMaterial!.id}`, payload, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / (e.total ?? 1))),
          })
        : await apiClient.post('/v1/practical-materials', payload, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / (e.total ?? 1))),
          });

      if (response.data.success) {
        showToast('success', isEditing ? 'Material Updated' : 'Material Published', isEditing ? 'Practical material updated successfully.' : 'Practical material created successfully.');
        onSuccess?.();
        setTimeout(() => onHide(), 900);
      } else {
        showToast('error', 'Error', response.data.message || 'Operation failed');
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || `HTTP ${err.response?.status}: Failed to save practical material.`;
      showToast('error', 'Error', msg);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    setFormData({ title: '', body: '', status: 'active', order: 1, video_links: [''] });
    setUploadedFiles([]);
    setErrors({});
    setError(null);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onHide();
  };

  const totalSize     = getTotalFilesSize();
  const canUploadMore = uploadedFiles.length < MAX_FILES && totalSize < MAX_TOTAL_SIZE;

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '90vw', maxWidth: '900px' }}
      dismissableMask
      draggable={false}
      pt={{
        root:    { style: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(14,34,93,0.25)' } },
        header:  { style: { display: 'none' } },
        content: { style: { padding: '22px' } },
      }}
    >
      <Toast ref={toast} position="top-right" />

      <div>
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div style={{
          background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 50%, ${C.navyDark} 100%)`,
          borderRadius: '12px', padding: '21px', marginBottom: '18px',
          color: C.white, position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: '-38px', right: '-38px', width: '135px', height: '135px', background: C.goldDim, borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '-30px', left: '20%', width: '90px', height: '90px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(30px)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 9px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '15px', marginBottom: '9px', fontSize: '0.51rem', fontWeight: '700', letterSpacing: '0.6px', textTransform: 'uppercase', color: C.gold }}>
              <i className="pi pi-book" style={{ fontSize: '0.53rem' }} />
              Practical Material Management
            </div>
            <h2 style={{ margin: '0 0 5px 0', fontSize: '1.13rem', fontWeight: '700', color: C.white, letterSpacing: '-0.5px' }}>
              {editingMaterial ? 'Edit Practical Material' : 'Create Practical Material'}
            </h2>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.64rem' }}>
              {chapterId ? 'Attached to the selected chapter' : 'General material — applies to the whole course, no specific chapter'}
            </p>
          </div>
        </div>

        {error && <Message severity="error" text={error} style={{ marginBottom: '18px', fontSize: '0.81rem' }} />}

        {/* ── Form ────────────────────────────────────────────────────── */}
        <div style={{ background: C.white, borderRadius: '14px', padding: '22px', marginBottom: '18px', border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(14,34,93,0.06)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>

            {/* Title + Order */}
            <div style={{ display: 'flex', gap: '11px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <GlobalInputField
                  label="Material Title"
                  required
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="e.g., Titration Lab Worksheet"
                  error={errors.title}
                />
              </div>
              <div style={{ width: '110px' }}>
                <label style={{ display: 'block', fontSize: '0.77rem', fontWeight: '600', color: C.navy, marginBottom: '5px' }}>
                  Order <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="number"
                  min={1}
                  value={formData.order}
                  onChange={(e) => handleInputChange('order', parseInt(e.target.value) || 1)}
                  style={{ width: '100%', padding: '10px 12px', border: `1px solid ${errors.order ? '#dc2626' : C.border}`, borderRadius: '7px', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box', color: C.navy, background: C.white }}
                />
                {errors.order && <small style={{ color: '#dc2626', marginTop: '4px', display: 'block', fontSize: '0.68rem' }}>{errors.order}</small>}
              </div>
            </div>

            {/* Status */}
            <GlobalDropdownField
              label="Status"
              required
              value={formData.status}
              onChange={(val) => handleInputChange('status', val)}
              options={statusOptions}
              placeholder="Select status"
              error={errors.status}
            />

            {/* Content */}
            <GlobalTextArea
              label="Material Content"
              required
              value={formData.body}
              onChange={(e) => handleInputChange('body', e.target.value)}
              placeholder="Instructions, procedure, expected outcomes..."
              error={errors.body}
              rows={7}
            />

            {/* External Video Links */}
            <SectionBox
              title="External Video Links"
              icon="pi-link"
              badge={`${formData.video_links.filter((l) => l.trim()).length}/5`}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {formData.video_links.map((link, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <GlobalInputField value={link} onChange={(e) => handleVideoLinkChange(index, e.target.value)} placeholder="https://youtube.com/watch?v=..." error="" />
                    {formData.video_links.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeVideoLink(index)}
                        style={{ padding: '5px 11px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', flexShrink: 0, fontSize: '0.68rem', fontWeight: '700', transition: 'all 0.2s ease' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
                      >
                        <i className="pi pi-trash" style={{ fontSize: '0.75rem' }} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {errors.video_links && <small style={{ color: '#dc2626', marginTop: '8px', display: 'block', fontSize: '0.68rem' }}>{errors.video_links}</small>}
              {formData.video_links.length < 5 && (
                <button
                  type="button"
                  onClick={addVideoLink}
                  style={{ marginTop: '12px', padding: '9px 14px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '8px', color: C.navy, cursor: 'pointer', fontSize: '0.79rem', fontWeight: '600', width: '100%', transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.background = 'rgba(251,178,31,0.04)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.white; }}
                >
                  <i className="pi pi-plus" style={{ marginRight: '6px' }} /> Add Another Video Link
                </button>
              )}
            </SectionBox>

            {/* File Attachments */}
            <SectionBox
              title="File Attachments"
              icon="pi-paperclip"
              badge={`${uploadedFiles.length}/${MAX_FILES} · ${formatFileSize(totalSize)} / ${formatFileSize(MAX_TOTAL_SIZE)}`}
            >
              <input
                ref={fileInputRef} type="file" multiple
                accept=".pdf,.xls,.xlsx,.doc,.docx,.jpg,.jpeg,.png"
                onChange={handleFileSelect} style={{ display: 'none' }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!canUploadMore}
                style={{
                  width: '100%', padding: '16px',
                  border: `2px dashed ${canUploadMore ? C.goldBorder : C.border}`,
                  borderRadius: '9px',
                  background: canUploadMore ? 'rgba(251,178,31,0.04)' : C.bg,
                  color: canUploadMore ? C.navy : C.muted,
                  cursor: canUploadMore ? 'pointer' : 'not-allowed',
                  fontSize: '0.79rem', fontWeight: '600',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => { if (canUploadMore) { e.currentTarget.style.background = 'rgba(251,178,31,0.08)'; e.currentTarget.style.borderColor = C.gold; } }}
                onMouseLeave={(e) => { if (canUploadMore) { e.currentTarget.style.background = 'rgba(251,178,31,0.04)'; e.currentTarget.style.borderColor = C.goldBorder; } }}
              >
                <i className="pi pi-upload" style={{ fontSize: '0.81rem', color: canUploadMore ? C.gold : C.muted }} />
                {canUploadMore ? 'Choose files (max 20MB total)' : 'Upload limit reached'}
              </button>
              <p style={{ margin: '7px 0 0 0', fontSize: '0.65rem', color: C.muted, textAlign: 'center' }}>
                Allowed: PDF, Word, Excel, JPEG, PNG
              </p>

              {uploadedFiles.length > 0 && (
                <div style={{ marginTop: '13px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
                  {uploadedFiles.map((fileObj, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 13px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '9px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '11px', flex: 1 }}>
                        <div style={{ width: '32px', height: '32px', background: C.bg, borderRadius: '7px', border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <i className={getFileIcon(fileObj.name)} style={{ fontSize: '1rem', color: C.navy }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: '0 0 3px 0', fontSize: '0.77rem', fontWeight: '700', color: C.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fileObj.name}</p>
                          <p style={{ margin: 0, fontSize: '0.65rem', color: C.muted, fontWeight: '600' }}>{formatFileSize(fileObj.size)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveFile(index)}
                        style={{ padding: '5px 11px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '0.68rem', fontWeight: '700', transition: 'all 0.2s ease', flexShrink: 0 }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </SectionBox>

          </div>
        </div>

        {loading && uploadProgress > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '0.75rem', color: C.sub }}>{uploadProgress < 100 ? 'Uploading to server...' : 'Processing...'}</span>
              <span style={{ fontSize: '0.75rem', color: C.navy, fontWeight: '700' }}>{uploadProgress}%</span>
            </div>
            <div style={{ background: C.bg, borderRadius: '8px', height: '8px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${uploadProgress}%`, background: `linear-gradient(90deg, ${C.navy}, ${C.navyMid})`, borderRadius: '8px', transition: 'width 0.3s ease' }} />
            </div>
          </div>
        )}

        {/* ── Actions ─────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: '11px', justifyContent: 'flex-end' }}>
          <GlobalButton label="Cancel" variant="outline" onClick={handleClose} disabled={loading} />
          <GlobalButton
            label={loading ? (uploadProgress < 100 ? `Uploading ${uploadProgress}%...` : editingMaterial ? 'Updating...' : 'Creating...') : editingMaterial ? 'Update Material' : 'Create Material'}
            variant="primary"
            onClick={handleSubmit}
            disabled={loading}
            loading={loading}
          />
        </div>
      </div>
    </Dialog>
  );
};

// ── SectionBox ──────────────────────────────────────────────────────────────
const SectionBox: React.FC<{ title: string; icon: string; badge?: string; children: React.ReactNode }> = ({ title, icon, badge, children }) => (
  <div style={{ background: C.bg, borderRadius: '13px', padding: '16px', border: `1px solid ${C.border}` }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
        <div style={{ width: '29px', height: '29px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className={`pi ${icon}`} style={{ color: C.gold, fontSize: '0.77rem' }} />
        </div>
        <span style={{ fontSize: '0.79rem', fontWeight: '700', color: C.navy }}>{title}</span>
      </div>
      {badge && <span style={{ fontSize: '0.65rem', color: C.muted, fontWeight: '600' }}>{badge}</span>}
    </div>
    {children}
  </div>
);

export default AddPracticalMaterialModal;